// Live talk to a camera's speaker through NX (core talk.ts), behind the same hold-T / hold-the-button gesture as the
// recorded push-to-talk (ptt.ts), which it stands in for when the site has no speaker devices of its own. While held,
// the microphone streams to the speaker as 16 kHz s16le PCM over a WebSocket; what is said is kept with the event.
//   - The microphone opens at once and its audio is held until the channel is ready, so the first words aren't lost.
//   - A tap (released within TAP_MS) says nothing: the console opens the talk-down drawer, as before.
//   - What ends it: letting go, Esc (cancel), the page hidden or the window losing focus (ConsoleScreen), MAX_MS, the
//     server's own cap, the speaker's channel closing. Every end stops the microphone (the browser's mic light goes off).

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText } from '../api.ts';
import type { Ptt, PttState } from './ptt.ts';
import { TAP_MS } from './ptt.ts';
import workletUrl from './pcm-worklet.js?url&no-inline';

const MAX_MS = 120_000;

export interface LiveTarget {
  eventId: string;
  cameraId: string;
  label: string;
}

/** The talk socket's address on this origin. */
export const talkSocketUrl = (path: string, origin = location.origin) => `${origin.replace(/^http/, 'ws')}${path}`;

function micError(e: unknown): { text: string; denied: boolean } {
  const name = e instanceof DOMException ? e.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return { text: 'Microphone blocked. Allow the microphone for this site in the browser (address bar → site settings), then hold T again.', denied: true };
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return { text: 'No microphone found. Plug in a headset, then hold T again.', denied: false };
  if (name === 'NotReadableError') return { text: 'The microphone is busy in another app. Close it there, then hold T again.', denied: false };
  return { text: `Could not start talking: ${errorText(e)}`, denied: false };
}

export function useLiveTalk(onDone: (t: { eventId: string; label: string; ms: number; simulated: boolean }) => void, onFail: (msg: string) => void): Ptt & { pressLive: (t: LiveTarget) => void } {
  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof AudioWorkletNode !== 'undefined' && typeof WebSocket !== 'undefined';
  const [st, setSt] = useState<PttState>({ phase: 'idle', startedAt: null, target: null, error: null, denied: false });
  const run = useRef<{ stop: (why: 'release' | 'cancel' | 'fail', msg?: string) => void; pressedAt: number } | null>(null);
  const cbs = useRef({ onDone, onFail });
  cbs.current = { onDone, onFail };

  useEffect(() => () => run.current?.stop('cancel'), []);
  // A hidden page never keeps talking.
  useEffect(() => {
    const hide = () => document.visibilityState === 'hidden' && run.current?.stop('release');
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, []);

  const pressLive = useCallback(
    (target: LiveTarget) => {
      if (run.current) return;
      if (!supported) {
        const msg = 'This browser cannot stream the microphone. Use a library or typed message.';
        setSt((s) => ({ ...s, error: msg }));
        cbs.current.onFail(msg);
        return;
      }
      const pressedAt = Date.now();
      let mic: MediaStream | null = null;
      let ctx: AudioContext | null = null;
      let ws: WebSocket | null = null;
      let ready = false;
      let simulated = false;
      let startedAt: number | null = null;
      let ended = false;
      const held: ArrayBuffer[] = [];
      const cap = setTimeout(() => stop('release'), MAX_MS);
      const stop = (why: 'release' | 'cancel' | 'fail', msg?: string) => {
        if (ended) return;
        ended = true;
        run.current = null;
        clearTimeout(cap);
        mic?.getTracks().forEach((t) => t.stop());
        void ctx?.close().catch(() => undefined);
        if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, why);
        setSt({ phase: 'idle', startedAt: null, target: null, error: why === 'fail' ? (msg ?? null) : null, denied: false });
        if (why === 'fail' && msg) cbs.current.onFail(msg);
        else if (why === 'release' && startedAt) cbs.current.onDone({ eventId: target.eventId, label: target.label, ms: Date.now() - startedAt, simulated });
      };
      run.current = { stop, pressedAt };
      setSt({ phase: 'opening', startedAt: null, target: { eventId: target.eventId, deviceIds: [], label: target.label }, error: null, denied: false });

      // Microphone and channel open side by side; audio captured before the channel is ready waits in `held`.
      const micUp = navigator.mediaDevices
        .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
        .then(async (s) => {
          mic = s;
          if (ended) return s.getTracks().forEach((t) => t.stop());
          ctx = new AudioContext();
          await ctx.audioWorklet.addModule(workletUrl);
          const node = new AudioWorkletNode(ctx, 'pcm16', { processorOptions: { rate: 16000 } });
          node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
            if (ended) return;
            if (ready && ws?.readyState === WebSocket.OPEN) ws.send(e.data);
            else if (held.length < 250) held.push(e.data); // up to 5 s while the channel opens
          };
          ctx.createMediaStreamSource(s).connect(node);
        });
      const channel = api.talkStart(target.eventId, target.cameraId).then(
        (r) =>
          new Promise<void>((resolve, reject) => {
            if (ended) return resolve();
            const sock = new WebSocket(talkSocketUrl(r.url));
            ws = sock;
            sock.binaryType = 'arraybuffer';
            sock.onmessage = (e) => {
              if (typeof e.data !== 'string') return;
              const m = JSON.parse(e.data) as { ready?: boolean; simulated?: boolean };
              if (!m.ready) return;
              ready = true;
              simulated = !!m.simulated;
              for (const b of held.splice(0)) sock.send(b);
              resolve();
            };
            sock.onclose = (e) => {
              if (!ready) reject(new Error(e.reason || 'the speaker channel did not open'));
              else if (!ended) stop(e.code === 1000 ? 'release' : 'fail', e.code === 1000 ? undefined : `Live talk ended: ${e.reason || 'the connection dropped'}.`);
            };
          }),
      );
      Promise.all([micUp, channel]).then(
        () => {
          if (ended) return;
          startedAt = Date.now();
          setSt((s) => ({ ...s, phase: 'recording', startedAt }));
        },
        (e: unknown) => {
          const m = e instanceof DOMException ? micError(e) : { text: `Live talk not started: ${errorText(e)}`, denied: false };
          stop('fail', m.text);
          setSt((s) => ({ ...s, denied: m.denied }));
        },
      );
    },
    [supported],
  );

  const release = useCallback((): 'tap' | 'sent' | 'none' => {
    const r = run.current;
    if (!r) return 'none';
    const tap = Date.now() - r.pressedAt < TAP_MS;
    r.stop(tap ? 'cancel' : 'release');
    return tap ? 'tap' : 'sent';
  }, []);
  const cancel = useCallback(() => run.current?.stop('cancel'), []);
  const clearError = useCallback(() => setSt((s) => ({ ...s, error: null, denied: false })), []);
  const press = useCallback(() => undefined, []);

  return { ...st, supported, press, pressLive, release, cancel, clearError };
}
